import { FC, useCallback, useEffect, useState } from 'react';
import { Alert, Button, CircularProgress, MenuItem, TextField, Typography } from '@mui/material';
import { PlayArrow, Refresh } from '@mui/icons-material';
import { api } from 'services/api';
import { getPgErrorMessage, getPgErrorStatus } from 'services/api/paymentGateway/errors';
import { TPgReconciliationReport, TPgReconciliationScope } from 'services/api/paymentGateway/types';
import { notificationContainer } from 'utils/notificationContainer';
import { PgConfirmDialog } from '../common/PgConfirmDialog';
import { PgFields } from '../common/PgFields';
import { PgMiniTable } from '../common/PgMiniTable';
import { PgPanel } from '../common/PgPanel';
import { humanize } from '../common/tableHelpers';
import { formatDateTime } from '../utils';

const SCOPES: TPgReconciliationScope[] = [
	'full',
	'open_payments',
	'settlements',
	'refunds',
	'disputes',
	'fx',
	'consents',
];

const SCOPE_LABELS: Record<TPgReconciliationScope, string> = {
	full: 'Full',
	open_payments: 'Open payments',
	settlements: 'Settlements',
	refunds: 'Refunds',
	disputes: 'Disputes',
	fx: 'FX conversions',
	consents: 'Consents',
};

const Report: FC<{ report: TPgReconciliationReport }> = ({ report }) => (
	<>
		<PgFields
			items={[
				['Scope', humanize(report.scope)],
				['Started by', humanize(report.trigger)],
				['Started', formatDateTime(report.startedAt)],
				['Finished', formatDateTime(report.finishedAt)],
				['Checked', report.checked],
				['Changed', report.changed],
				['Errors', report.errors],
				['Disputes read', report.disputesSucceeded ? 'Yes' : 'No'],
			]}
		/>
		{report.errors > 0 && (
			<Alert severity="warning" sx={{ mt: 2 }}>
				The run had errors: see the notes below and the gateway log.
			</Alert>
		)}
		{report.notes.length > 0 && (
			<Typography variant="body2" component="div" sx={{ mt: 2 }}>
				{report.notes.map((note) => (
					<div key={note}>• {note}</div>
				))}
			</Typography>
		)}
		<Typography variant="subtitle2" color="primary" sx={{ mt: 2, mb: 0.5 }}>
			Areas
		</Typography>
		<PgMiniTable
			rows={report.areas}
			getKey={(row) => row.area}
			columns={[
				{ header: 'Area', render: (row) => humanize(row.area) },
				{ header: 'Skipped', render: (row) => (row.skipped ? 'Yes' : 'No') },
				{ header: 'Checked', align: 'right', render: (row) => row.checked },
				{ header: 'Changed', align: 'right', render: (row) => row.changed },
				{ header: 'Errors', align: 'right', render: (row) => row.errors },
				{ header: 'Notes', render: (row) => row.notes.join('; ') || '—' },
			]}
		/>
	</>
);

/**
 * Reconciliation with Airwallex (03 §4.13): runs one area or everything and shows the last report
 * (every run also writes a `reconciliation.report` entry to the audit log).
 */
export const ReconciliationView: FC = () => {
	const [scope, setScope] = useState<TPgReconciliationScope>('full');
	const [report, setReport] = useState<TPgReconciliationReport | null>(null);
	const [loading, setLoading] = useState(false);
	const [notFound, setNotFound] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [running, setRunning] = useState(false);
	const [runError, setRunError] = useState<string | null>(null);

	const loadLast = useCallback(async () => {
		setLoading(true);
		setError(null);
		setNotFound(false);
		try {
			setReport(await api.paymentGateway.getLastReconciliation());
		} catch (e) {
			setReport(null);
			if (getPgErrorStatus(e) === 404) setNotFound(true);
			else setError(getPgErrorMessage(e, 'Failed to load the last report'));
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		void loadLast();
	}, [loadLast]);

	const run = async () => {
		setRunning(true);
		setRunError(null);
		try {
			setReport(await api.paymentGateway.runReconciliation({ scope }));
			setNotFound(false);
			setError(null);
			setConfirmOpen(false);
			notificationContainer('Reconciliation finished', 'success');
		} catch (e) {
			setRunError(getPgErrorMessage(e, 'The reconciliation failed'));
		} finally {
			setRunning(false);
		}
	};

	return (
		<>
			<PgPanel
				title="Reconciliation with Airwallex"
				actions={
					<>
						<TextField
							select
							size="small"
							label="Scope"
							value={scope}
							onChange={(e) => setScope(e.target.value as TPgReconciliationScope)}
							sx={{ minWidth: 180, backgroundColor: 'background.paper' }}
						>
							{SCOPES.map((value) => (
								<MenuItem key={value} value={value}>
									{SCOPE_LABELS[value]}
								</MenuItem>
							))}
						</TextField>
						<Button
							size="small"
							variant="contained"
							startIcon={<PlayArrow />}
							onClick={() => {
								setRunError(null);
								setConfirmOpen(true);
							}}
						>
							Run
						</Button>
						<Button
							size="small"
							onClick={() => void loadLast()}
							disabled={loading}
							startIcon={loading ? <CircularProgress size={14} /> : <Refresh />}
						>
							Last report
						</Button>
					</>
				}
			>
				{error && <Alert severity="error">{error}</Alert>}
				{notFound && (
					<Typography variant="body2" color="text.secondary">
						There is no report yet. Run the reconciliation.
					</Typography>
				)}
				{report && <Report report={report} />}
			</PgPanel>
			<PgConfirmDialog
				open={confirmOpen}
				title="Run the reconciliation"
				confirmLabel="Run"
				busy={running}
				error={runError}
				onConfirm={() => void run()}
				onClose={() => setConfirmOpen(false)}
				description={`The gateway compares ${SCOPE_LABELS[scope].toLowerCase()} with Airwallex and fixes the differences it finds (statuses, settlements, refunds). The same check runs on schedule; a full run may take a while.`}
			/>
		</>
	);
};
